import { ApiErrorCode, hasFieldErrors, isApiError } from '@/shared/api';
import { i18n } from '@/shared/config';

/** Коды, для которых у клиента есть свой текст (errors:codes.*). */
const KNOWN_CODES = [
  ApiErrorCode.ValidationError,
  ApiErrorCode.Unauthorized,
  ApiErrorCode.Forbidden,
  ApiErrorCode.NotFound,
  ApiErrorCode.Conflict,
  ApiErrorCode.RateLimited,
  ApiErrorCode.ServiceUnavailable,
  ApiErrorCode.NetworkError,
  ApiErrorCode.UnexpectedResponse,
  ApiErrorCode.ClientUpdateRequired,
] as const;
type KnownCode = (typeof KNOWN_CODES)[number];

function isKnownCode(code: string): code is KnownCode {
  return KNOWN_CODES.some((known) => known === code);
}

/**
 * Локализованное сообщение об ошибке (ТЗ API-04): для общих кодов — свой текст клиента,
 * для кодов модулей — `message` сервера (уже на языке запроса), иначе — общий текст.
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // 409 `conflict` сервер объясняет конкретно («На экране должен остаться хотя бы один пункт.») —
    // его текст (уже на языке запроса) полезнее общего; без конверта сообщение — «HTTP 409».
    if (
      error.code === ApiErrorCode.Conflict &&
      error.message &&
      !error.message.startsWith('HTTP ')
    ) {
      return error.message;
    }
    if (isKnownCode(error.code)) return i18n.t(`errors:codes.${error.code}`);
    if (error.message) return error.message;
  }
  return i18n.t('errors:generic');
}

/**
 * Ошибка действия вне формы (кнопка в строке, смена статуса): у `422`/`409` с `details.fields` —
 * тексты сервера по полям (уже на языке запроса: «Обложка — JPG или PNG от 1600 px»), иначе —
 * {@link getErrorMessage}. Глобальный тост `422` не показывает — его показывает экран.
 */
export function getActionErrorMessage(error: unknown): string {
  if (hasFieldErrors(error)) {
    const messages = [...new Set(error.fieldErrors.map((item) => item.message.trim()))].filter(
      (message) => message.length > 0,
    );
    if (messages.length > 0) return messages.join(' ');
  }
  return getErrorMessage(error);
}
